import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { UserAssignment, AssignmentRole } from "@/types/database";

/** Fetch all assignments for a given org (used by strata admin on Team page) */
export function useOrgAssignments(orgId: string | null) {
  return useQuery({
    queryKey: ["user_assignments", "org", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_assignments")
        .select(`
          *,
          profile:profiles(id, email, full_name, role, phone, avatar_url, created_at, updated_at)
        `)
        .eq("org_id", orgId!);
      if (error) throw error;
      return (data ?? []) as (UserAssignment & { profile: any })[];
    },
  });
}

/** Fetch all assignments for the currently logged-in user */
export function useMyAssignments() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["user_assignments", "mine", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_assignments")
        .select(`
          *,
          organization:organizations(id, name, address, total_units, plan, admin_id, created_at, updated_at)
        `)
        .eq("user_id", user!.id);
      if (error) throw error;
      return (data ?? []) as UserAssignment[];
    },
  });
}

/** Remove a user assignment (strata admin revokes access) */
export function useRemoveAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase
        .from("user_assignments")
        .delete()
        .eq("id", assignmentId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user_assignments"] });
    },
  });
}

/** Update permissions on an assignment */
export function useUpdateAssignmentPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      assignmentId,
      permissions,
    }: {
      assignmentId: string;
      permissions: Record<string, boolean>;
    }) => {
      const { error } = await supabase
        .from("user_assignments")
        .update({ permissions })
        .eq("id", assignmentId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user_assignments"] });
    },
  });
}

/** Create an assignment directly (used after invite acceptance) */
export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      orgId,
      role,
      unitId,
      permissions = {},
    }: {
      userId: string;
      orgId: string;
      role: AssignmentRole;
      unitId?: string;
      permissions?: Record<string, boolean>;
    }) => {
      const { data, error } = await supabase
        .from("user_assignments")
        .insert({ user_id: userId, org_id: orgId, role, unit_id: unitId ?? null, permissions })
        .select()
        .single();
      if (error) throw error;
      return data as UserAssignment;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user_assignments"] });
    },
  });
}
